/* import __COLOCATED_TEMPLATE__ from './fin-custom.hbs'; */
/* RESPONSIBLE TEAM: team-ml */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type FinCustom from 'embercom/models/operator/visual-builder/step/fin-custom';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type FinCustomConfiguration from 'embercom/objects/visual-builder/configuration/step/fin-custom';
import type InsertableAction from 'embercom/models/workflow-connector/insertable-action';
import { action } from '@ember/object';
import type AttributeDescriptor from 'embercom/models/operator/visual-builder/attribute-descriptor';
import {
  type SourceObject,
  Type,
} from 'embercom/models/operator/visual-builder/attribute-descriptor';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type ConnectionPoint from 'embercom/models/operator/visual-builder/connection-point';
import { type ComponentLike } from '@glint/template';
import type { TaskGenerator, Yieldable } from 'ember-concurrency';
import type AttributeDescriptorListOption from 'embercom/models/operator/visual-builder/attribute-descriptor-list-option';
import generateUUID from 'embercom/lib/uuid-generator';
import type IntlService from 'ember-intl/services/intl';
import type LogService from 'embercom/services/log-service';
import { type Block } from 'embercom/models/common/blocks/block';
import { taskFor } from 'ember-concurrency-ts';
import { task } from 'ember-concurrency-decorators';
import CommandsResolver from 'embercom/lib/workflows/commands-resolver';
import { post } from 'embercom/lib/ajax';
import { showFinTaskSimpleMode } from 'embercom/lib/workflows/helpers/fin-task';
import { EntityType } from 'embercom/models/data/entity-types';
import { APP_ATTRIBUTES } from 'embercom/lib/common/template-attribute-resolver';
import type Admin from 'embercom/models/admin';
import type Tag from 'embercom/models/tag';
import createFragmentFromBlock from 'embercom/lib/blocks/create-fragment-from-block';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import { extractTemplateVariables } from 'embercom/lib/workflows/template-variables';
import { camelize } from '@ember/string';
import { hasFeature } from 'embercom/helpers/has-feature';
import type { AllChannels } from 'embercom/lib/operator/custom-bots/constants';

export function camelizeKeys(hash: object): object | null {
  if (!hash) {
    return null;
  }

  let entries = Object.entries(hash);
  let camelizedEntries = entries.map(([key, value]) => {
    let camelizedValue = value;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      camelizedValue = camelizeKeys(value);
    }
    return [camelize(key), camelizedValue];
  });
  return Object.fromEntries(camelizedEntries);
}

export interface ListOption {
  id: string;
  value: string;
}

export interface OutputParam {
  id?: string;
  name: string;
  description: string;
  descriptorType: Type;
  listOptions: ListOption[];

  attributeType?: string;

  // For conversation attributes
  multiline?: boolean;
}

// Attribute and AttributeCollection types are, upsettingly, also defined in:
// apps/teammate-app/src/features/fin-testing-suite/utils/attributes.ts
export interface Attribute {
  identifier: string;
  name: string;
  type: string;
  description: string;
  category: 'user' | 'conversation' | 'temporary' | 'company';
}

export interface AttributeCollection {
  userAttributes: Attribute[];
  companyAttributes: Attribute[];
  conversationAttributes: Attribute[];
  temporaryAttributes: Attribute[];
  referencedAttributeIdentifiers: string[];
}

export interface EscalationDestination {
  id: string;
  name: string;
  avatar?: {
    color: string;
    imageUrls: {
      square25: string;
      square50: string;
      square128: string;
    };
    initials: string;
  };
}

export interface EscalationDestinationsGroup {
  heading?: string;
  attributes: EscalationDestination[];
}

export interface EscalationDestinationsCollection {
  defaultTeam: EscalationDestinationsGroup;
  teams: EscalationDestinationsGroup;
  teammates: EscalationDestinationsGroup;
}

export interface FinTasksEditorPayload {
  workflowId: string;
  appId: string;
  targetChannels: AllChannels[];
  allowedAttributes: AttributeCollection;
  escalationDestinations: EscalationDestinationsCollection;
  step: Partial<FinCustom>;
  outputParams: OutputParam[];
  readOnly: boolean;
  attributesConfig: $TSFixMe;
  commandsConfig: {
    actions: any[];
    tags: any[];
    teammates: any[];
  };
  isDraft: boolean;
  description?: string;
  isRulesetLive: boolean;
  hasUnsavedChanges?: () => boolean;
  maybeSaveRuleset?: () => Promise<void>;
  onSyncStepData?: (data: any) => void;
  hasLocalChanges?: boolean;
  isProcedure: boolean;
  getCurrentSnapshotId?: () => string | null;
  getDraftSnapshotId?: () => string | null;
}

interface Arguments {
  step: FinCustom;
  stepConfig: FinCustomConfiguration;
  editorState: EditorState;
  readOnly: boolean;
  isTemplatePreview: boolean;
  connectionPointComponent: ComponentLike<{ connectionPoint: ConnectionPoint }>;
  updateConnectionPoints: (node: Node) => Generator<Yieldable<void>, void, unknown>;
  isDraft: boolean;
}

function referencedAttributeTemplatableFields(step: FinCustom): Array<string> {
  let instructions = step.instructions || [];

  return instructions.flatMap((instruction: Block) => {
    return extractTemplateVariables(instruction, true);
  });
}

export function allowedAttributesForTestConfigPanel(
  attributeService: $TSFixMe,
  step: FinCustom,
): AttributeCollection {
  let userAttributes: Array<{
    identifier: string;
    name: string;
    description: string;
    displayable: boolean;
    type: string;
    templatable_identifier: string;
    options?: any;
  }> = attributeService.userAttributes;

  let companyAttributes: Array<{
    identifier: string;
    name: string;
    description: string;
    displayable: boolean;
    type: string;
    templatable_identifier: string;
    options?: any;
  }> = attributeService.companyAttributes.map((attr: any) => attr.attribute); // don't know why this extra layer exists for company attrs

  let conversationAttributes: Array<{
    identifier: string;
    description: string;
    name: string;
    type: string;
    templatable_identifier: undefined; // doesn't exist here
    options?: any;
  }> = attributeService.conversationAttributes;

  let temporaryAttributes: Array<{
    identifier: string;
    name: string;
    description: string;
    type: string;
    templatable_identifier: string;
    options?: any;
  }> = step.editorConfig?.localAttributes ?? [];
  return {
    userAttributes: userAttributes
      .filter((attr) => attr.displayable)
      .map((attr) => ({
        identifier: attr.identifier,
        name: attr.name,
        description: attr.description,
        type: attr.type,
        listOptions: buildListOptions(attr.options),
        category: 'user',
      })),
    companyAttributes: companyAttributes
      .filter((attr) => attr.displayable)
      .map((attr) => ({
        identifier: attr.identifier,
        name: attr.name,
        description: attr.description,
        type: attr.type,
        listOptions: buildListOptions(attr.options),
        category: 'company',
      })),
    conversationAttributes: conversationAttributes.map((attr) => ({
      identifier: attr.identifier,
      name: attr.name,
      description: attr.description,
      type: attr.type,
      listOptions: buildListOptions(attr.options),
      category: 'conversation',
    })),
    temporaryAttributes: temporaryAttributes.map((attr) => ({
      identifier: attr.identifier,
      name: attr.name,
      description: attr.description || '',
      type: attr.type,
      listOptions: buildListOptions(attr.options),
      category: 'temporary',
    })),
    referencedAttributeIdentifiers: ((): string[] => {
      let allAttributes = [
        ...userAttributes,
        ...companyAttributes,
        ...conversationAttributes,
        ...temporaryAttributes,
      ];
      let attributeIdentifiersByTemplatableFields: Record<string, string> = {};

      allAttributes.forEach((attr) => {
        // if templatable_identifier is not defined, the identifer is equal to templatable_identifier
        let templatableField = attr.templatable_identifier ?? attr.identifier;
        attributeIdentifiersByTemplatableFields[templatableField] = attr.identifier;
      });
      let referenceIdentifiers = referencedAttributeTemplatableFields(step).map((field) => {
        return attributeIdentifiersByTemplatableFields[field];
      });
      // filter out any that could not be looked up and deduplicate
      let filtered = referenceIdentifiers.filter((a) => a != null);
      return [...new Set(filtered)];
    })(),
  };
}

// options is inconsistent in the Ember data store, sometimes it's AttributeOption, others AttributeDescriptorListOption
// we support both here
function buildListOptions(options: any): ListOption[] {
  if (!options) {
    return [];
  }
  return options.map((option: any) => ({
    id: option.id,
    value: option.label ?? option.value,
  }));
}

function mentionedEscalationDestinations(step: FinCustom): Set<string> {
  let mentioned = new Set<string>();
  let instructions = step.instructions || [];

  // Extract template variables from all instruction blocks
  let templateVariables = instructions.flatMap((instruction) => {
    return extractTemplateVariables(instruction, true);
  });

  // Filter for assignee variables (escalation destinations)
  templateVariables.forEach((variable) => {
    if (variable.startsWith('assignee.')) {
      mentioned.add(variable);
    }
  });

  return mentioned;
}

export function escalationDestinationsForTestConfigPanel(
  stepConfig: FinCustomConfiguration,
  step: FinCustom,
) {
  let resolver = stepConfig.commandsResolver;
  let mentionedDestinations = mentionedEscalationDestinations(step);
  // Filter helper - always include default, plus any mentioned destinations
  let filterDestinations = (destinations: any[], isDefaultTeam = false) => {
    let filtered = destinations.filter((attr: any) => {
      if (isDefaultTeam) {
        return true;
      }

      return mentionedDestinations.size !== 0 && mentionedDestinations.has(attr.identifier);
    });

    return filtered.map((attr: any) => ({
      id: attr.identifier.split('.')[1] ?? attr.identifier,
      name: attr.name,
      avatar: attr.object?.avatar ? camelizeKeys(attr.object.avatar) : undefined,
    }));
  };

  return {
    defaultTeam: {
      ...resolver.defaultTeam,
      attributes: filterDestinations(resolver.defaultTeam.attributes, true),
    },
    teams: {
      ...resolver.teams,
      attributes: filterDestinations(resolver.teams.attributes, false),
    },
    teammates: {
      ...resolver.teammates,
      attributes: filterDestinations(resolver.teammates.attributes, false),
    },
  };
}

export default class FinCustomNodeStep extends Component<Arguments> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare modalService: $TSFixMe;
  @service declare attributeService: $TSFixMe;
  @service declare contentEditorService: $TSFixMe;
  @service declare logService: LogService;
  @service declare session: $TSFixMe;
  @service declare notificationsService: $TSFixMe;

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);

    if (this.args.step.outwardConnectionPoints.length === 0 && !this.args.step.isAgentic) {
      this.args.step.outwardConnectionPoints.addObject(
        this.store.createRecord('operator/visual-builder/connection-point', { type: 'fallback' }),
      );
    }
  }

  get app() {
    return this.appService.app;
  }

  get allowedAttributes(): AttributeCollection {
    return allowedAttributesForTestConfigPanel(this.attributeService, this.args.step);
  }

  get escalationDestinations() {
    return escalationDestinationsForTestConfigPanel(this.args.stepConfig, this.args.step);
  }

  get stepName() {
    return this.args.step.name;
  }

  get validationErrors() {
    return this.args.step.validations.errors;
  }

  get firstGoalBlock() {
    let blocks = this.args.step.blocks?.serialize() ?? [];
    blocks = blocks.filter((block: Block) => !!block.text);

    if (blocks.length > 0) {
      return blocks[0];
    }
  }

  get shouldShowError() {
    return this.args.editorState.shouldShowValidations && !this.args.step.validations.isValid;
  }

  get shouldShowOutputError() {
    return (
      this.args.editorState.shouldShowValidations &&
      !this.args.step.validations.attrs.outwardConnectionPoints.isValid
    );
  }

  get shouldShowFallbackBranch() {
    return !this.args.step.isAgentic;
  }

  get fallbackBranch(): ConnectionPoint {
    return this.args.step.outwardConnectionPoints.firstObject!;
  }

  get outputParams() {
    return this.args.step.finOutputParams.map((descriptor: AttributeDescriptor) => {
      return {
        name: descriptor.name,
        description: descriptor.description,
        id: descriptor.id,
        descriptorType: descriptor.type,
        listOptions: descriptor.listOptions
          .toArray()
          .map((option: AttributeDescriptorListOption) => {
            return {
              id: option.id,
              value: option.label,
            };
          }) as ListOption[],
      } as OutputParam;
    });
  }

  get showFinTaskSimpleMode() {
    return showFinTaskSimpleMode(
      this.appService.app,
      this.args.editorState,
      this.args.isTemplatePreview,
    );
  }

  @action
  async addOutputParam(param: OutputParam) {
    let attributeDescriptor: any;

    switch (param.attributeType) {
      case 'conversation':
        attributeDescriptor = await this.createConversationAttribute(param);
        break;
      default:
        attributeDescriptor = this.createOrUpdateTemporaryAttribute(param);
    }

    return attributeDescriptor;
  }

  workflowIsValid() {
    if (hasFeature('workflow-draft-state')) {
      // Use the new additionalData approach with safe navigation
      return (
        this.args.editorState.workflow.additionalData?.title &&
        this.args.editorState.workflow.additionalData?.description
      );
    } else {
      // Fallback to contentEditorService approach
      return (
        this.contentEditorService.activeObject?.title &&
        this.contentEditorService.activeObject?.description
      );
    }
  }

  buildFinTasksEditorPayload(): FinTasksEditorPayload {
    let payload = {
      workflowId: this.args.editorState.workflow.workflowInstanceEntityId,
      workflowIsValid: this.workflowIsValid(),
      allowedAttributes: this.allowedAttributes,
      escalationDestinations: this.escalationDestinations,
      step: {
        id: this.args.step.id,
        externalStepId: this.args.step.externalStepId,
        instructions:
          this.args.step.finProcedureContent?.content?.length > 0
            ? this.args.step.finProcedureContent.content
            : this.args.step.instructions,
        sections: this.args.step.finProcedureContent?.sections ?? [],
        guideline: this.args.step.guideline.map((block: Block) => block.serialize()),
        guidelineCategories: this.args.step.guidelineCategories ?? [],
        backgroundExecution: this.args.step.backgroundExecution ?? false,
        completeWhen: this.args.step.blocks?.serialize() ?? [],
        description: this.contentEditorService.activeObject?.description,
      },
      attributesConfig: this.buildAttributesForFinTasksEditor(),
      commandsConfig: this.buildCommandsConfig(),
      readOnly: this.args.readOnly,
      appId: this.appService.app.id,
      targetChannels: this.contentEditorService.activeObject?.targetChannels || [],
      workflowInstanceId: this.args.editorState.workflow.workflowInstanceId,
      externalStepId: this.args.step.externalStepId,
      isDraft: this.args.isDraft,
      isRulesetLive: this.contentEditorService.ruleset.isLive,
      isProcedure: this.contentEditorService.activeObject?.botData?.isProcedure,
    };

    // Force JSON serialization/deserialization to ensure it's strictly JSON
    let serializedPayload = JSON.parse(JSON.stringify(payload));

    serializedPayload.hasUnsavedChanges = () => {
      return this.contentEditorService.activeRulesetLink.hasUnsavedChanges;
    };

    serializedPayload.getCurrentSnapshotId = () => {
      return this.contentEditorService.activeObject?.visualBuilderObject?.id ?? null;
    };

    serializedPayload.getDraftSnapshotId = () => {
      return this.contentEditorService.activeObject?.draftVisualBuilderObject?.id ?? null;
    };

    serializedPayload.maybeSaveRuleset = async () => {
      if (this.appService.app.canUseFeature('fin-procedures-pioneer-demo')) {
        try {
          // Save directly without confirmation modal by calling ruleset.save()
          await this.contentEditorService.ruleset.save();

          this.notificationsService.notifyConfirmation(
            this.intl.t('operator.workflows.visual-builder.fin-custom-settings.save-success'),
          );
        } catch (error) {
          console.error('[FinCustom] maybeSaveRuleset error:', error);
          this.notificationsService.notifyResponseError(error, {
            default: this.intl.t(
              'operator.workflows.visual-builder.fin-custom-settings.save-error',
            ),
          });
        }
      } else {
        await this.contentEditorService.saveRuleset.perform({ skipConfirmation: true });
      }
    };

    serializedPayload.hasUnsavedDraft = () => {
      if (this.appService.app.canUseFeature('workflow-draft-state')) {
        return this.contentEditorService.activeObject?.draftVisualBuilderObject?.hasUnsavedChanges;
      }
      return false;
    };

    serializedPayload.maybeSaveAsDraft = async () => {
      if (!this.appService.app.canUseFeature('workflow-draft-state')) {
        return;
      }

      await this.contentEditorService.saveAsDraft.perform();
    };

    serializedPayload.onSyncStepData = (
      data: FinTasksEditorPayload,
    ): Partial<FinTasksEditorPayload['attributesConfig']> => {
      // Only update if there were actual changes in React
      if (data?.step && data?.hasLocalChanges) {
        this.updateStepFromReactData(data.step);
      }

      return {
        allLocalAttributes: this.getLocalAttributes(),
      };
    };

    return serializedPayload;
  }

  get customObjectAttributesFlattened() {
    return this.attributeService.customObjectAttributeGroups.flatMap((customObject: any) =>
      customObject.attributes.map((attr: any) => {
        return {
          name: attr.name,
          identifier: attr.identifier,
          templatable_identifier: attr.templatable_identifier,
          type: attr.type,
          description: attr.description,
          archived: attr.archived,
          templatable: attr.templatable,
          customObjectHeading: customObject.heading,
          isCustomObjectAttribute: true, // needed to group attributes in the frontend
        };
      }),
    );
  }

  buildAttributesForFinTasksEditor() {
    let attributeInfoResolver = this.createAttributeInfoResolver();
    let updateableAttributeInfoResolver = this.args.stepConfig.attributeInfoResolveForUpdates;
    let customAttributes = this.buildCustomAttributes(attributeInfoResolver);

    return {
      attributes:
        this.args.step.editorConfig?.runtimeMatchingTargetingAttributes
          ?.filter((group: any) => group.heading !== 'Temporary attributes')
          ?.map(this.convertAttributeGroupForReact) || [],
      updateableAttributes: (updateableAttributeInfoResolver?.attributes || []).map(
        this.convertAttributeToPlainObject,
      ),
      includeAppAttributes: true,
      customObjectAttributes: this.customObjectAttributesFlattened,
      includeCustomObjectAttributes: true,
      includeFileAttributes: this.args.step.appService.app.canUseFinCustomImageRecognition,
      manualAppAttributes: APP_ATTRIBUTES,
      showCategoryPrefixes: false,
      customActions: this.args.stepConfig.finCustomActionsGroupList(),
      localAttributes: this.getLocalAttributesExcludingArchived(),
      allLocalAttributes: this.getLocalAttributes(),
      ...customAttributes,
      workflowInstanceId: this.args.editorState.workflow.workflowInstanceId,
    };
  }

  private createAttributeInfoResolver() {
    return new AttributeInfoResolver({
      attributes:
        this.args.step.editorConfig?.runtimeMatchingTargetingAttributes
          .map((group) => group.attributes)
          .flat() ?? this.attributeService.userEditorMetadataAndConversationAttributes,
      customObjectAttributes: this.customObjectAttributesFlattened,
      includeAppAttributes: true,
      includeCustomObjectAttributes: true,
      includeFileAttributes: this.args.step.appService.app.canUseFinCustomImageRecognition,
      localAttributes: this.getLocalAttributesExcludingArchived(),
      allLocalAttributes: this.getLocalAttributes(),
      customActions: this.args.stepConfig.finCustomActionsGroupList(),
      showCategoryPrefixes: false,
    });
  }

  private getLocalAttributes() {
    return (
      this.args.editorState.workflow.attributeDescriptors
        .toArray()
        .map(this.convertAttributeWithIdentifiers) || []
    );
  }

  private getLocalAttributesExcludingArchived() {
    return (
      this.args.editorState.workflow.attributeDescriptors
        .filter((attr: any) => !attr.archived)
        .toArray()
        .map(this.convertAttributeWithIdentifiers) || []
    );
  }

  private convertAttributeWithIdentifiers = (attr: any) => {
    // Convert to plain object to avoid circular references
    let plainAttr = this.convertAttributeToPlainObject(attr);
    let isWorkflowLocalVariable = attr.templatable_identifier?.startsWith(
      'workflow_local_variables.',
    );
    return {
      ...plainAttr,
      identifier: attr.identifier,
      templatable_identifier: attr.templatable_identifier,
      isTemporary: isWorkflowLocalVariable,
    };
  };

  private convertAttributeGroupForReact = (group: any) => {
    return {
      heading: group.heading,
      attributes: (group.attributes || []).map(this.convertAttributeToPlainObject),
    };
  };

  private convertAttributeToPlainObject = (attr: any) => {
    let plainAttr;
    if (attr.serialize) {
      plainAttr = attr.serialize();
    } else {
      plainAttr = attr;
    }

    // Normalise list_options to listOptions for React consistency
    let normalizedAttr = { ...plainAttr };
    if (normalizedAttr.list_options) {
      normalizedAttr.listOptions = normalizedAttr.list_options;
      delete normalizedAttr.list_options;
    }

    return {
      ...normalizedAttr,
      isUser: attr.isUser,
      isCompany: attr.isCompany,
      isConversation: attr.isConversation,
      isTemporary: attr.isTemporary,
      isApp: attr.isApp,
      isUserEvent: attr.isUserEvent,
      referenceOnly: attr.referenceOnly,
    };
  };

  private buildCustomAttributes(attributeInfoResolver: AttributeInfoResolver) {
    let customAttributes = {
      customConversationAttributes: [],
      ticketAttributes: [],
      ticketTypeAttributes: [],
    };

    if (
      !attributeInfoResolver.includeCustomObjectAttributes &&
      !attributeInfoResolver.includeCustomActionAttributes
    ) {
      return customAttributes;
    }

    if (attributeInfoResolver.includeCustomObjectAttributes) {
      let conversationAttributes = attributeInfoResolver.includeFileAttributes
        ? this.attributeService.nonArchivedConversationCustomAttributesWithFileS3Key
        : this.attributeService.nonArchivedConversationCustomAttributesWithoutFile;

      customAttributes.customConversationAttributes = conversationAttributes.map(
        this.convertAttributeToPlainObject,
      );
    }

    if (attributeInfoResolver.includeCustomActionAttributes) {
      let conversationAttributes = attributeInfoResolver.includeFileAttributes
        ? this.attributeService.conversationAttributesForCustomActionWithPresignedUrl
        : this.attributeService.conversationAttributesForCustomAction;

      customAttributes.customConversationAttributes = conversationAttributes.map(
        this.convertAttributeToPlainObject,
      );
      customAttributes.ticketAttributes = this.attributeService.ticketAttributesForCustomAction.map(
        this.convertAttributeToPlainObject,
      );
      customAttributes.ticketTypeAttributes = this.attributeService.ticketAttributesByType.map(
        (ticketTypeAttribute: any) => ({
          category: ticketTypeAttribute.category,
          heading: ticketTypeAttribute.heading,
          attributes: ticketTypeAttribute.attributes.map(this.convertAttributeToPlainObject),
        }),
      );
    }

    return customAttributes;
  }

  buildCommandsConfig() {
    return {
      actions: this.getCustomActions(),
      tags: this.getTags(),
      teammates: this.getTeammates(),
      teams: this.getTeams(),
      defaultTeam: this.getDefaultTeam(),
    };
  }

  getCustomActions() {
    let customActions = this.store
      .peekAll('workflow-connector/insertable-action')
      .filter((action: InsertableAction) => action.state === 'live' && !action.isTemplate)
      .filter(
        (action: InsertableAction) =>
          action.usage === 'fin' || this.appService.app.canUseConsolidatedActions,
      )
      .map((action: InsertableAction) => ({
        identifier: `workflow_connector.${action.id}`,
        templatable_identifier: `workflow_connector.${action.id}`,
        name: action.name,
        composer_name: this.args.step.isV3
          ? action.name
          : this.intl.t(
              'operator.workflows.visual-builder.fin-custom-settings.configure.prompt.custom-action',
              { action_name: action.name },
            ),
        type: 'string',
        icon: 'webhook',
        noFallback: true,
      }));

    return customActions;
  }

  getTags() {
    let tags = this.store
      .peekAll('tag')
      .filter((tag: Tag) => !tag.archived)
      .map((tag: Tag) => ({
        identifier: `tag.${tag.id}`,
        templatable_identifier: `tag.${tag.id}`,
        name: tag.name,
        composer_name: tag.name,
      }));

    return tags;
  }

  getTeammates() {
    if (this.appService.app.canUseStandalone) {
      return [];
    }
    let teammates = (this.appService.app.assignableHumanAdmins || []).map((teammate: Admin) => ({
      id: teammate.id,
      identifier: `assignee.${teammate.id}`,
      templatable_identifier: `assignee.${teammate.id}`,
      name: teammate.name,
      composer_name: teammate.name,
      avatar: teammate.avatar,
    }));

    return teammates;
  }

  getTeams() {
    if (this.appService.app.canUseStandalone) {
      return [];
    }
    return this.appService.app.assignableTeams.map((team: any) => ({
      id: team.id,
      identifier: `assignee.${team.id}`,
      templatable_identifier: `assignee.${team.id}`,
      name: team.name,
      composer_name: team.name,
      avatar: team.avatar,
    }));
  }

  getDefaultTeam() {
    return {
      identifier: `assignee.DEFAULT`,
      id: 'DEFAULT',
      templatable_identifier: `assignee.DEFAULT`,
      name: this.intl.t(
        'operator.workflows.visual-builder.fin-custom-settings.inline-attribute.default-team-header',
      ),
    };
  }

  get commandsResolver() {
    return new CommandsResolver(this.buildCommandsConfig());
  }

  @action
  openFinTasksEditorSheet() {
    this.modalService.openModal('react/workflows/fin-tasks-editor-sheet', {
      open: true,
      finTasksEditorPayload: this.buildFinTasksEditorPayload(),
      onClose: (data: FinTasksEditorPayload) => {
        // Only update if there were actual changes in React
        if (data?.step && data?.hasLocalChanges) {
          this.updateStepFromReactData(data.step);
        }
        this.modalService.closeModal();
      },
    });
  }

  /**
   * Gets the current step from the active workflow using externalStepId.
   * This ensures we always work with the latest step object, even after
   * the workflow has been saved and recreated with new IDs.
   */
  private getCurrentStep(externalStepId: string): FinCustom {
    let currentStep = this.args.step;
    let activeWorkflow = this.contentEditorService.activeObject?.activeWorkflow;

    if (!activeWorkflow) {
      return currentStep;
    }

    activeWorkflow.groups.forEach((group: any) => {
      group.steps.forEach((step: any) => {
        if (step.externalStepId === externalStepId) {
          currentStep = step;
        }
      });
    });

    return currentStep;
  }

  @action
  updateStepFromReactData(stepData: any) {
    // Get the current step using externalStepId to avoid stale references
    let currentStep = this.args.step;
    if (
      this.appService.app.canUseFeature('fin-procedures-pioneer-demo') ||
      this.appService.app.canUseFeature('workflow-draft-state')
    ) {
      currentStep = this.getCurrentStep(stepData.externalStepId);
    }

    // Temporary logs to cache Ember and React state for flaky data losses
    this.logService.log({
      log_type: 'updateStepFromReactDataStart',
      app_id: this.appService.app?.id,
      admin_id: this.session.teammate?.id,
      stepData,
      final_ember_state: {
        stepId: currentStep.id,
        stepName: currentStep.name,
        stepHasDirtyAttributes: currentStep.hasDirtyAttributes,
        finProcedureContent: {
          exists: !!currentStep.finProcedureContent,
          content: currentStep.finProcedureContent?.content,
          fragmentId: currentStep.finProcedureContent?.id,
          fragmentHasDirtyAttributes: currentStep.finProcedureContent?.hasDirtyAttributes,
        },
      },
    });
    let updatableProperties = ['name'] as const;
    let updatedProperties: string[] = [];

    updatableProperties.forEach((property) => {
      if (stepData[property] !== null) {
        currentStep.set(property, stepData[property]);
        updatedProperties.push(property);
      }
    });

    if (stepData.instructions !== null) {
      if (!currentStep.finProcedureContent) {
        currentStep.finProcedureContent = this.store.createFragment(
          'operator/visual-builder/step/fin-custom/procedure-content',
          { content: [] },
        );
      }

      currentStep.finProcedureContent.content = stepData.instructions;
    }

    if (stepData.sections !== null && stepData.sections !== undefined) {
      if (!currentStep.finProcedureContent) {
        currentStep.finProcedureContent = this.store.createFragment(
          'operator/visual-builder/step/fin-custom/procedure-content',
          { content: [] },
        );
      }

      currentStep.finProcedureContent.sections = stepData.sections;
    }

    if (stepData.guideline !== null) {
      let guidelineBlocks = stepData.guideline.map((block: Block) =>
        createFragmentFromBlock(this.store, block),
      );
      currentStep.set('guideline', guidelineBlocks);
      updatedProperties.push('guideline');
    }

    if (stepData.guidelineCategories !== null && stepData.guidelineCategories !== undefined) {
      currentStep.set('guidelineCategories', stepData.guidelineCategories);
      updatedProperties.push('guidelineCategories');
    }

    if (stepData.backgroundExecution !== null && stepData.backgroundExecution !== undefined) {
      currentStep.set('backgroundExecution', stepData.backgroundExecution);
      updatedProperties.push('backgroundExecution');
    }

    if (stepData.completeWhen !== null) {
      let completeWhenBlocks = stepData.completeWhen.map((block: Block) =>
        createFragmentFromBlock(this.store, block),
      );
      currentStep.set('blocks', completeWhenBlocks);
      updatedProperties.push('blocks');
    }

    if (stepData.newLocalAttributes && stepData.newLocalAttributes.length > 0) {
      let newAttributes = stepData.newLocalAttributes.map((attrData: any) => {
        let testParam: OutputParam = {
          id: attrData.id,
          name: attrData.name,
          description: attrData.description,
          descriptorType: attrData.type,
          listOptions: attrData.listOptions || [],
        };

        return this._createTemporaryAttribute(
          testParam,
          attrData.referenceOnly,
          attrData.source_object,
        );
      });

      newAttributes.forEach((attribute: AttributeDescriptor) => {
        newAttributes
          .filter(
            (attr: AttributeDescriptor) =>
              attr.sourceObject && attr.sourceObject.id === attribute.id,
          )
          .forEach((childDescriptor: AttributeDescriptor) => {
            attribute._childDescriptors.pushObject(childDescriptor);
          });
      });

      let newActionOutputAttributeDescriptorParameterMapping: Record<string, string> = {};

      newAttributes.forEach((attribute: AttributeDescriptor) => {
        if (
          attribute.sourceObject?.type === EntityType.WorkflowConnectorAction &&
          attribute.sourceObject.id
        ) {
          newActionOutputAttributeDescriptorParameterMapping[attribute.sourceObject.id.toString()] =
            attribute.id;
        }
      });

      currentStep.set('actionOutputAttributeDescriptorIdentifierMapping', {
        ...currentStep.actionOutputAttributeDescriptorIdentifierMapping,
        ...newActionOutputAttributeDescriptorParameterMapping,
      });
    }

    if (stepData.updatedLocalAttributes !== null) {
      stepData.updatedLocalAttributes.forEach((attrData: any) => {
        let updateParam: OutputParam = {
          id: attrData.id,
          name: attrData.name,
          description: attrData.description,
          descriptorType: attrData.type,
          listOptions: attrData.listOptions || [],
        };
        this._updateTemporaryAttribute(updateParam);
      });
    }

    if (stepData.deletedLocalAttributes && stepData.deletedLocalAttributes.length > 0) {
      let existingActionOutputAttributeDescriptorIdentifierMapping = {
        ...currentStep.actionOutputAttributeDescriptorIdentifierMapping,
      };

      stepData.deletedLocalAttributes.forEach((attrData: any) => {
        this.removeOutputParam(attrData);

        if (
          attrData.source_object?.type === EntityType.WorkflowConnectorAction &&
          attrData.source_object.id
        ) {
          delete existingActionOutputAttributeDescriptorIdentifierMapping[
            attrData.source_object.id.toString()
          ];
        }
      });

      currentStep.set(
        'actionOutputAttributeDescriptorIdentifierMapping',
        existingActionOutputAttributeDescriptorIdentifierMapping,
      );
    }

    currentStep.referencedAttributeIdentifiers = stepData.referencedAttributeIdentifiers;

    if (stepData.newLocalAttributes || stepData.deletedLocalAttributes) {
      currentStep.notifyPropertyChange('actionOutputAttributeDescriptorIdentifierMapping');
    }

    if (hasFeature('fin-procedures-pioneer-demo')) {
      if (stepData.description !== null && stepData.description !== undefined) {
        this.contentEditorService.activeObject.set('description', stepData.description);
        updatedProperties.push('description');
      }
    }

    // Temporary logs to cache Ember and React state for flakey data losses
    this.logService.log({
      log_type: 'updateStepFromReactDataEnd',
      app_id: this.appService.app?.id,
      admin_id: this.session.teammate?.id,
      final_ember_state: {
        finProcedureContent: {
          exists: !!currentStep.finProcedureContent,
          content: currentStep.finProcedureContent?.content,
          fragmentHasDirtyAttributes: currentStep.finProcedureContent?.hasDirtyAttributes,
        },
        stepHasDirtyAttributes: currentStep.hasDirtyAttributes,
      },
    });
  }

  @action
  updateOutputParam(param: OutputParam) {
    this.args.step.finOutputParams = this.args.step.finOutputParams.map((descriptor) => {
      if (descriptor.id !== param.id) {
        return descriptor;
      }

      this.updateDescriptorProperties(descriptor, param);

      descriptor.listOptions = this.buildUpdatedListOptions(descriptor, param.listOptions);
      return descriptor;
    }) as $TSFixMe;
  }

  @action
  copyActionOutputParam(action: InsertableAction) {
    let identifier = this.args.step.actionOutputAttributeDescriptorIdentifier;

    if (identifier) {
      let existingAttributeDescriptor = this.store.peekRecord(
        'operator/visual-builder/attribute-descriptor',
        identifier,
      );
      this.args.editorState.deleteLocalVariable(existingAttributeDescriptor);
    }

    if (
      action.actionOutputParameterDescriptor &&
      action.actionOutputParameterDescriptor.sourcePath &&
      action.actionOutputParameterDescriptor.type
    ) {
      let sourceObject = {
        type: EntityType.WorkflowConnectorAction,
        id: action.id,
        path: action.actionOutputParameterDescriptor.sourcePath,
      };

      let newAttributeDescriptor = this.args.editorState.copyActionLocalVariable(
        action.actionOutputParameterDescriptor,
        action.name,
        sourceObject,
      );

      if (newAttributeDescriptor) {
        this.args.step.actionOutputAttributeDescriptorIdentifier =
          newAttributeDescriptor.identifier;
      }
    }
  }

  private updateDescriptorProperties(descriptor: AttributeDescriptor, param: OutputParam) {
    if (descriptor.name !== param.name) {
      descriptor.name = param.name;
    }
    if (descriptor.description !== param.description) {
      descriptor.description = param.description;
    }
    if (descriptor.type !== param.descriptorType) {
      descriptor.type = param.descriptorType;
    }
  }

  private buildUpdatedListOptions(descriptor: AttributeDescriptor, newOptions: ListOption[]) {
    return newOptions.map((option) => {
      if (option.id) {
        let existingOption = descriptor.listOptions.findBy('id', option.id);
        if (existingOption) {
          if (existingOption.label !== option.value) {
            existingOption.label = option.value;
          }
          return existingOption;
        }
      }

      return this.store.createRecord('operator/visual-builder/attribute-descriptor-list-option', {
        id: generateUUID(),
        label: option.value,
        descriptor,
      });
    });
  }

  private async createConversationAttribute(param: OutputParam) {
    let attributeDescriptor = this.store.createRecord('conversation-attributes/descriptor', {
      name: param.name,
      description: param.description,
      dataType: param.descriptorType,
      multiline: param.multiline,
    });

    if (param.descriptorType === 'list') {
      param.listOptions.forEach((option) => {
        attributeDescriptor.listOptions.createFragment({
          listOptionId: generateUUID(),
          label: option.value,
        });
      });
    }

    await attributeDescriptor.save();
    return attributeDescriptor;
  }

  private createOrUpdateTemporaryAttribute(param: OutputParam) {
    if (param.id) {
      return this._updateTemporaryAttribute(param);
    } else {
      return this._createTemporaryAttribute(param);
    }
  }

  private _createTemporaryAttribute(
    param: OutputParam,
    referenceOnly?: boolean,
    sourceObject?: SourceObject,
  ) {
    let attributeDescriptor = this.args.editorState.createLocalVariable(
      param.name,
      param.descriptorType ?? Type.string,
      referenceOnly,
      sourceObject,
      param.id,
    );
    attributeDescriptor.description = param.description;

    if (
      attributeDescriptor.type === 'list' ||
      attributeDescriptor.type === 'workflow_attribute_descriptor_list'
    ) {
      this._updateTemporaryAttributeListOptions(param, attributeDescriptor, true);
    }

    if (!sourceObject) {
      this.args.step.finOutputParams.addObject(attributeDescriptor);
    }

    return attributeDescriptor;
  }

  private _updateTemporaryAttribute(param: OutputParam) {
    let existingAttrDescriptor = this.args.step.finOutputParams.findBy('id', param.id);
    if (!existingAttrDescriptor) {
      return;
    }
    existingAttrDescriptor.name = param.name;
    existingAttrDescriptor.type = param.descriptorType;
    existingAttrDescriptor.description = param.description;

    if (
      existingAttrDescriptor.type === 'list' ||
      existingAttrDescriptor.type === 'workflow_attribute_descriptor_list'
    ) {
      this._updateTemporaryAttributeListOptions(param, existingAttrDescriptor, false);
    }

    return existingAttrDescriptor;
  }

  private _updateTemporaryAttributeListOptions(
    param: OutputParam,
    attributeDescriptor: AttributeDescriptor,
    isNewAttribute: boolean,
  ) {
    for (let option of param.listOptions ?? []) {
      if (option.id) {
        let listOption = attributeDescriptor.listOptions.findBy('id', option.id);
        if (listOption) {
          listOption.label = option.value;
        }
      } else {
        let listOption = this.store.createRecord(
          'operator/visual-builder/attribute-descriptor-list-option',
          {
            id: generateUUID(),
            label: option.value,
            descriptor: attributeDescriptor,
          },
        );

        attributeDescriptor.listOptions.pushObject(listOption);
      }
    }

    if (!isNewAttribute) {
      attributeDescriptor.listOptions = attributeDescriptor.listOptions.filter((option) =>
        param.listOptions.some((newOption) => newOption.value === option.label),
      );
    }
  }

  @action
  removeOutputParam(outputParam: OutputParam) {
    if (!outputParam.id) {
      return;
    }

    let attributeDescriptor = this.store.peekRecord(
      'operator/visual-builder/attribute-descriptor',
      outputParam.id,
    );

    if (!attributeDescriptor) {
      this.logService.log({
        log_type: 'removeOutputParam:attributeDescriptorNotFound',
        app_id: this.appService.app?.id,
        stepId: this.args.step.id,
        workflowId: this.args.editorState.workflow.id,
        workflowInstanceId: this.args.editorState.workflow.workflowInstanceId,
        ...outputParam,
      });
      return;
    }

    this.args.step.finOutputParams = this.args.step.finOutputParams.filter(
      (param: AttributeDescriptor) => param.id !== outputParam.id,
    ) as $TSFixMe;

    this.args.editorState.deleteLocalVariable(attributeDescriptor);

    this.args.step.notifyPropertyChange('finOutputParams');
  }

  get isSummarizingStep() {
    return taskFor(this.performInstructionBlockSummarizationTask).isRunning;
  }

  @action
  performInstructionBlockSummarization(step: FinCustom) {
    if (this.args.readOnly || !step.hasUnsavedChanges) {
      return;
    }

    let hasEmptyInstructions = step.instructions.every((instructionBlocks) =>
      instructionBlocks.every((block: Block) => !block.hasContent),
    );

    if (hasEmptyInstructions) {
      step.name = null;
      return;
    }

    taskFor(this.performInstructionBlockSummarizationTask).perform(step);
  }

  @task({ enqueue: true })
  *performInstructionBlockSummarizationTask(step: FinCustom): TaskGenerator<void> {
    let response = yield post(`/ember/operator_workflows/summarize_fin_task_instructions`, {
      app_id: this.app.id,
      instructions: step.instructions,
      existing_summary: step.name,
    });

    if (response.outcome === 'failure' || !response.summary) {
      return;
    }

    step.name = response.summary;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::FinCustom': typeof FinCustom;
    'workflows/graph-editor/node-items/steps/fin-custom': typeof FinCustom;
  }
}
